import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-sl-200 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-ev-500 text-white hover:bg-ev-600 active:bg-ev-700 disabled:bg-ev-100 disabled:text-ev-300",
        secondary:
          "bg-sl-100 text-sl-950 hover:bg-sl-200 active:bg-sl-300 disabled:bg-sl-100 disabled:text-sl-300",
        outline:
          "border border-sl-500 bg-transparent text-sl-950 shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:border-sl-700 hover:bg-sl-50 active:border-sl-700 active:bg-sl-100 disabled:border-sl-200 disabled:text-sl-300 disabled:shadow-none",
        ghost:
          "bg-transparent text-sl-950 hover:bg-sl-100 active:bg-sl-200 disabled:text-sl-300",
        destructive:
          "bg-re-200 text-white hover:bg-re-300 active:bg-re-400 disabled:bg-re-50 disabled:text-re-100",
        subtle:
          "bg-sl-50 text-sl-700 hover:bg-sl-100 active:bg-sl-200 disabled:bg-sl-50 disabled:text-sl-300",
        success:
          "bg-fe-400 text-sl-50 hover:bg-fe-500 active:bg-fe-600 disabled:bg-fe-200 disabled:text-fe-50/80",
        vibrant:
          "bg-ev-500 text-white hover:bg-ev-600 active:bg-ev-700 disabled:bg-ev-100 disabled:text-ev-300",
        icon: "bg-sl-50 text-sl-500 hover:bg-sl-100 active:bg-sl-200 disabled:bg-sl-50 disabled:text-sl-300",
        cta: "bg-[#212330] text-[#f6f6f9] hover:bg-[#212330]/90 active:bg-[#212330] disabled:bg-[#ebecf3] disabled:text-[#212330] disabled:opacity-50",
      },
      size: {
        xs: "min-h-6 px-3 py-1 text-xs font-medium",
        sm: "min-h-9 px-4 py-2 text-sm font-semibold",
        md: "min-h-10 px-5 py-2.5 text-[0.95rem] font-semibold",
        lg: "min-h-12 px-5 py-3 text-[0.95rem] font-semibold tracking-tight",
        xl: "min-h-14 px-6 py-4 text-[1.05rem] font-bold tracking-tight",
        icon: "size-10 p-0",
      },
      rounded: { true: "rounded-full", false: "rounded-lg" },
    },
    defaultVariants: { variant: "primary", size: "md", rounded: false },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, rounded }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
